import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnChanges, Optional, Output } from '@angular/core';

import { EventParticipantImage } from '@frontend/sports/betting-offer/feature/model';
import { ImagesConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { OptionalNonRootToken } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import { FallbackImage, PARTICIPANT_IMAGE_CONFIG, ParticipantImageConfig } from '@frontend/sports/participant-image/feature/provider';
import { UtilsService } from '@frontend/vanilla/core';

const sportsToReversePlayer2Image: number[] = [SportConstant.Icehockey, SportConstant.AmericanFootball, SportConstant.Baseball];

export enum ImageType {
    Logo = 'Logo',
    Jersey = 'Jersey',
    Headshot = 'Headshot',
    Fallback = 'Fallback',
}

export enum ImageRootPath {
    Live = 'live',
    Prod = 'prod',
}

export interface Image {
    type: ImageType;
    url: string;
}

export enum ParticipantType {
    Player = 'Player',
    Team = 'Team',
}

@Component({
    selector: 'ms-participant-image',
    styleUrl: 'participant-image.scss',
    templateUrl: 'participant-image.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, NgIf],
})
export class ParticipantImageComponent implements OnChanges {
    private readonly participantImageUrl: string;
    private readonly participantLogoUrl: string;
    private readonly fallbackImage: FallbackImage;

    @Input() participantImage?: EventParticipantImage;
    @Input() sportId: number;
    @Input() shouldRotate = false;
    @Input() showFallback = true;
    @Input() showLogoColor = false;
    @Input() participantType: ParticipantType;
    @Output() logoLoaded = new EventEmitter<boolean>();

    loaded = false;
    imageType = ImageType;
    imageClass = '';
    image?: Image;
    rotateImage = false;
    fallbackColor = false;
    backgroundColor?: string;
    showBackgroundCircle = false;

    private images: Image[];

    constructor(
        private imagesConfig: ImagesConfig,
        private utilsService: UtilsService,
        @Optional() @Inject(OptionalNonRootToken(PARTICIPANT_IMAGE_CONFIG)) private config: Partial<ParticipantImageConfig> | undefined,
    ) {
        this.participantImageUrl = this.imagesConfig.participantImageUrl;
        this.participantLogoUrl = this.imagesConfig.participantLogoUrl;
        this.fallbackImage = this.config?.fallbackImage ?? FallbackImage.Default;
    }

    ngOnChanges(): void {
        this.settingImage();
        this.showBackgroundCircle = this.showLogoColor && this.imagesConfig.isLogoBackgroundEnabled;
        this.fallbackColor = this.getFallbackColor();
        this.backgroundColor = this.getBackgroundColor();
    }

    private settingImage() {
        this.loaded = false;

        this.images = [];

        this.setHeadshotImage();
        this.images.push({ type: ImageType.Logo, url: this.getImageUrl(ImageType.Logo) });
        this.images.push({ type: ImageType.Jersey, url: this.getImageUrl(ImageType.Jersey) });

        if (this.showFallback) {
            this.images.push({ type: ImageType.Fallback, url: this.getImageUrl(ImageType.Fallback) });
        }

        this.images = this.images.filter((image) => hasValue(image.url));

        this.image = this.images.shift();

        this.rotateImage = this.shouldRotate && sportsToReversePlayer2Image.some((s) => s === this.sportId) && this.image?.type !== ImageType.Logo;
    }

    onImageFailedLoaded(): void {
        if (this.isPlayer) {
            this.image = this.images.at(-1);
            return;
        }

        this.image = this.images.shift();
        this.logoLoaded.emit(false);
    }

    get isPlayer(): boolean {
        return this.participantType === ParticipantType.Player;
    }

    private setHeadshotImage() {
        if (this.isPlayer) {
            if (this.participantImage?.headshot) {
                this.images.push({ type: ImageType.Headshot, url: this.getImageUrl(ImageType.Headshot) });
            } else {
                this.images.push({ type: ImageType.Fallback, url: this.getImageUrl(ImageType.Fallback) });
            }
        }
    }

    private getImageUrl(imageType: ImageType): string {
        let participantImageName;
        let participantUrl = this.participantImageUrl;

        switch (imageType) {
            case ImageType.Headshot:
                participantImageName = this.participantImage?.headshot;
                const headshotExtension = this.participantImage?.headshot?.split('.').pop();
                participantUrl =
                    [this.participantLogoUrl, this.participantImageUrl].find((url) => {
                        const ext = url.split('.').pop();
                        return ext === headshotExtension;
                    }) ?? this.participantImageUrl;
                break;
            case ImageType.Logo:
                participantImageName = this.participantImage && this.participantImage.logo;
                participantUrl = this.participantLogoUrl;
                break;
            case ImageType.Jersey:
                participantImageName = this.participantImage && this.participantImage.jersey;
                break;
            case ImageType.Fallback:
                if (this.isPlayer) {
                    participantImageName = FallbackImage.EpcotDefault;
                } else {
                    participantImageName = this.showFallback ? this.fallbackImage : '';
                }
                break;
        }

        const rootpath = this.getRootPath();
        const participantImageProfileName = participantImageName?.split('.')[0];

        return participantImageName ? this.utilsService.format(participantUrl, rootpath, this.sportId, participantImageProfileName) : '';
    }

    private getRootPath(): string {
        let rootpath;
        if (this.isPlayer && !this.participantImage?.isParticipantProfile) {
            rootpath = ImageRootPath.Prod;
        } else {
            rootpath = this.participantImage?.isParticipantProfile ? ImageRootPath.Prod : ImageRootPath.Live;
        }
        return rootpath;
    }

    private getBackgroundColor(): string | undefined {
        const logoColor = this.participantImage?.logoColor ? `#${this.participantImage.logoColor}` : undefined;
        if (this.isPlayer) {
            return logoColor;
        }
        if (this.showBackgroundCircle && this.participantImage?.isParticipantProfile) {
            return logoColor;
        }
        return undefined;
    }

    private getFallbackColor(): boolean {
        if (this.isPlayer) {
            return !this.participantImage?.logoColor;
        }
        return !!(this.showBackgroundCircle && this.participantImage?.isParticipantProfile && !this.participantImage?.logoColor);
    }
}
